import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useState,
  useSyncExternalStore,
} from "react";
import type { KeyboardShortcut } from "../../lib/keyboardShortcut";
import { MagicFormValidationError } from "../../lib/magicFormErrors";
import {
  findShortcutTarget,
  type MagicFormShortcutTarget,
  resolveSaveShortcut,
} from "../../lib/magicFormSaveShortcut";
import type { FormContextType } from "./MagicForm";

/**
 * A member form registered in a {@link MagicFormGroup}. Forms register
 * themselves (see `useRegisterMagicFormInGroup`); group-level save/reset
 * buttons act on all dirty members at once, so a user can edit several
 * atoms without noticing they are separate forms.
 */
export interface MagicFormGroupMember {
  id: string;
  /** Save order, ascending; ties resolve by registration order. */
  order: number;
  /** Optional human-readable name used in error reporting. */
  label?: string;
  isDirty: boolean;
  /**
   * The dirty state at call time, for callers that run before the member's
   * next render (`isDirty` is the value of its last render).
   */
  isDirtyNow?: () => boolean;
  loading: boolean;
  submit: (ignoreSuggestedChanges?: boolean) => Promise<void>;
  reset: () => Promise<void>;
  /** What the form's `validate` reports; `undefined` while it is valid. */
  validationErrors?: readonly string[];
}

export interface MagicFormGroupState {
  /** True when any member form has unsaved changes. */
  isDirty: boolean;
  dirtyCount: number;
  /** True while `saveAll` is running. */
  isSaving: boolean;
  /** True when any member form is still loading its atom. */
  isLoading: boolean;
  /**
   * The validation messages of the dirty members, in save order; empty while
   * they are valid. `saveAll` refuses to save while there are any.
   */
  validationErrors: readonly string[];
}

export interface MagicFormGroupContextType<
  Member extends MagicFormGroupMember = MagicFormGroupMember,
> {
  /** Upsert a member (forms re-register on every render to stay fresh). */
  register: (member: Member) => void;
  unregister: (id: string) => void;
  subscribe: (listener: () => void) => () => void;
  getState: () => MagicFormGroupState;
  /**
   * The dirty members in save order. With `live` it asks each member's
   * `isDirtyNow` where it has one, instead of the state of its last render.
   */
  getDirtyMembers: (options?: { live?: boolean }) => readonly Member[];
  /**
   * Save all dirty members sequentially in `(order, registration)` order.
   * Aborts on the first failure and rethrows: already-saved members stay
   * saved, the failed and remaining members keep their drafts. A
   * `MagicFormRevisionConflictError` is rethrown as-is so the failing
   * form's conflict modal takes over. While a dirty member is invalid (as
   * of its last render) it saves nothing and throws a
   * `MagicFormValidationError` with the messages of every invalid member. A
   * member made invalid in the same event, before it rendered, is still
   * refused by its own submit, after the members before it saved. `only`
   * narrows it to some members.
   */
  saveAll: (only?: (member: Member) => boolean) => Promise<void>;
  /** Reset all dirty members sequentially; `only` narrows it. */
  resetAll: (only?: (member: Member) => boolean) => Promise<void>;
  /**
   * Called by the group's save button to name itself as the element that the
   * group's save shortcut clicks (see `MagicFormGroup`'s `saveShortcut`);
   * returns the unregister function.
   */
  registerShortcutTarget: (target: MagicFormShortcutTarget) => () => void;
  /** The registered group save button that is in the document, if any. */
  getShortcutTarget: () => HTMLElement | undefined;
}

export const MagicFormGroupContext = createContext<
  MagicFormGroupContextType | undefined
>(undefined);

/**
 * The save shortcut of the surrounding group; `undefined` outside a
 * `MagicFormGroup` and when it is off.
 */
const MagicFormGroupSaveShortcutContext = createContext<
  KeyboardShortcut | undefined
>(undefined);

type Internal<Member> = Member & { registrationIndex: number };

const isDirtyNow = (member: MagicFormGroupMember) =>
  member.isDirtyNow?.() ?? member.isDirty;

const sameMessages = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((message, index) => message === b[index]);

/** Headless store behind {@link MagicFormGroup}; exported for reuse/testing. */
export function createMagicFormGroupStore<
  Member extends MagicFormGroupMember = MagicFormGroupMember,
>(): MagicFormGroupContextType<Member> {
  const members = new Map<string, Internal<Member>>();
  const listeners = new Set<() => void>();
  const shortcutTargets = new Set<MagicFormShortcutTarget>();
  let registrationCounter = 0;
  let isSaving = false;
  let state: MagicFormGroupState = {
    isDirty: false,
    dirtyCount: 0,
    isSaving: false,
    isLoading: false,
    validationErrors: [],
  };

  const recompute = () => {
    let dirtyCount = 0;
    let isLoading = false;
    for (const member of members.values()) {
      if (member.isDirty) dirtyCount++;
      if (member.loading) isLoading = true;
    }
    const next: MagicFormGroupState = {
      isDirty: dirtyCount > 0,
      dirtyCount,
      isSaving,
      isLoading,
      validationErrors: validationErrorsOfDirtyMembers(),
    };
    if (
      next.isDirty === state.isDirty &&
      next.dirtyCount === state.dirtyCount &&
      next.isSaving === state.isSaving &&
      next.isLoading === state.isLoading &&
      sameMessages(next.validationErrors, state.validationErrors)
    ) {
      return;
    }
    state = next;
    for (const listener of listeners) listener();
  };

  const sortedMembers = () =>
    [...members.values()].sort(
      (a, b) => a.order - b.order || a.registrationIndex - b.registrationIndex,
    );

  /** Each message once, in save order. */
  const validationErrorsOfDirtyMembers = (
    only: (member: Member) => boolean = () => true,
  ) => [
    ...new Set(
      sortedMembers().flatMap((member) =>
        member.isDirty && only(member) ? (member.validationErrors ?? []) : [],
      ),
    ),
  ];

  const runSequentially = async (
    action: (member: Member) => Promise<void>,
    only: (member: Member) => boolean = () => true,
  ) => {
    if (isSaving) return;
    isSaving = true;
    recompute();
    try {
      for (const snapshot of sortedMembers()) {
        // Re-read the live entry: earlier saves may have re-rendered later
        // forms and re-registered them with fresh state/closures. A member
        // that unregistered mid-run (its form unmounted) is gone from the map
        // and skipped — never called through a stale snapshot closure.
        const member = members.get(snapshot.id);
        if (!member || !isDirtyNow(member) || !only(member)) continue;
        await action(member);
      }
    } finally {
      isSaving = false;
      recompute();
    }
  };

  return {
    register: (member) => {
      const existing = members.get(member.id);
      members.set(member.id, {
        ...member,
        registrationIndex: existing?.registrationIndex ?? registrationCounter++,
      });
      recompute();
    },
    unregister: (id) => {
      members.delete(id);
      recompute();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getState: () => state,
    getDirtyMembers: ({ live = false } = {}) =>
      sortedMembers().filter((member) =>
        live ? isDirtyNow(member) : member.isDirty,
      ),
    saveAll: async (only) => {
      const validationErrors = validationErrorsOfDirtyMembers(only);
      if (validationErrors.length > 0) {
        throw new MagicFormValidationError(validationErrors);
      }
      await runSequentially((member) => member.submit(), only);
    },
    resetAll: (only) => runSequentially((member) => member.reset(), only),
    registerShortcutTarget: (target) => {
      shortcutTargets.add(target);
      return () => {
        shortcutTargets.delete(target);
      };
    },
    getShortcutTarget: () => findShortcutTarget(shortcutTargets),
  };
}

export interface MagicFormGroupProps {
  children: ReactNode;
  /**
   * The shortcut that clicks the group's save button while the focus is in a
   * member form without a save button of its own: ⌘S on macOS, Ctrl+S
   * elsewhere by default. `false` turns it off.
   */
  saveShortcut?: KeyboardShortcut | false;
}

/**
 * Provider that lets multiple MagicForms (bound to different atoms) share a
 * single pair of save/reset buttons. Forms rendered inside register
 * automatically; a form outside any group behaves exactly as before.
 */
export function MagicFormGroup({
  children,
  saveShortcut,
}: Readonly<MagicFormGroupProps>) {
  const [store] = useState(createMagicFormGroupStore);
  // A context of its own: a new shortcut object on a render re-renders only
  // the shortcut's readers, not every useMagicFormGroup and
  // useMagicFormGroupState consumer.
  return (
    <MagicFormGroupContext.Provider value={store}>
      <MagicFormGroupSaveShortcutContext.Provider
        value={resolveSaveShortcut(saveShortcut)}
      >
        {children}
      </MagicFormGroupSaveShortcutContext.Provider>
    </MagicFormGroupContext.Provider>
  );
}

/**
 * The save shortcut of the surrounding group (see `MagicFormGroup`'s
 * `saveShortcut`); `undefined` outside a group and when it is off.
 */
export function useMagicFormGroupSaveShortcut(): KeyboardShortcut | undefined {
  return useContext(MagicFormGroupSaveShortcutContext);
}

/** The surrounding group, or undefined when not rendered inside one. */
export function useMagicFormGroup(): MagicFormGroupContextType | undefined {
  return useContext(MagicFormGroupContext);
}

const EMPTY_GROUP_STATE: MagicFormGroupState = {
  isDirty: false,
  dirtyCount: 0,
  isSaving: false,
  isLoading: false,
  validationErrors: [],
};

/** Live aggregate state of the surrounding group (empty state without one). */
export function useMagicFormGroupState(): MagicFormGroupState {
  const group = useContext(MagicFormGroupContext);
  return useSyncExternalStore(
    group?.subscribe ?? (() => () => {}),
    group?.getState ?? (() => EMPTY_GROUP_STATE),
    group?.getState ?? (() => EMPTY_GROUP_STATE),
  );
}

export interface RegisterMagicFormInGroupOptions {
  context: Pick<
    FormContextType,
    | "draft"
    | "isDirty"
    | "loading"
    | "submit"
    | "resetDraft"
    | "validationErrors"
  >;
  groupOrder?: number;
  excludeFromGroup?: boolean;
  groupLabel?: string;
}

/**
 * Registers a MagicForm in the surrounding {@link MagicFormGroup} (no-op
 * without one). Upserts on every render so the group always sees the
 * current dirty state and fresh submit/reset closures.
 */
export function useRegisterMagicFormInGroup({
  context,
  groupOrder = 0,
  excludeFromGroup = false,
  groupLabel,
}: RegisterMagicFormInGroupOptions): void {
  const group = useContext(MagicFormGroupContext);
  const id = useId();

  // Intentionally no dependency array: upsert on every render so the group
  // always sees fresh dirty state and submit/reset closures.
  useEffect(() => {
    if (!group) return;
    if (excludeFromGroup) {
      group.unregister(id);
      return;
    }
    group.register({
      id,
      order: groupOrder,
      label: groupLabel,
      isDirty: context.isDirty,
      loading: context.loading,
      submit: context.submit,
      reset: context.resetDraft,
      validationErrors: context.validationErrors,
    });
  });

  useEffect(() => {
    if (!group) return;
    return () => group.unregister(id);
  }, [group, id]);
}
